/**
 * Lusty Chic Fit — Find My Perfect Fit quiz (Phase 5; Profile Schema V2 — usual UK
 * jeans size added for the deterministic Size Engine extension).
 * Namespace: window.LustyChicFit (reserved in Phase 1 / CLAUDE.md).
 * Storage key: lustychic_fit_profile (reserved in Phase 1 / CLAUDE.md).
 *
 * No network calls, no customer-account sync, no scoring — browser-local only, per the
 * Phase 1 architecture. If localStorage is unavailable or throws, the quiz still works
 * for the current session; it just cannot persist across reloads.
 *
 * Profile Schema V2: adds `usualUkSize` (number, e.g. 12 — never the string "UK 12").
 * A V1 profile (no `usualUkSize`) loaded here is never rewritten or reset — every
 * existing field is preserved untouched, and `_save()` simply stamps `version: 2` from
 * that point on. Fit Match, Results, and PDP Fit Match never depended on this field and
 * continue working identically whether or not it is present.
 *
 * UX correction (2026-09-20): `leg_shape` is now unlimited multi-select (an array,
 * e.g. ["Skinny","Straight"]) instead of a single string. A legacy single-string
 * profile is treated as a one-item selection wherever it is read here — never reset
 * or migrated destructively. `fit_goals` keeps its existing 3-max multi-select
 * behaviour unchanged; only the max-detection helper was generalised so any group
 * without a data-quiz-max attribute is genuinely uncapped rather than defaulting to 1.
 */
window.LustyChicFit = window.LustyChicFit || {};

(function (ns) {
  'use strict';

  var STORAGE_KEY = 'lustychic_fit_profile';

  var SHAPE_LABELS = {
    apple: 'Apple',
    pear: 'Pear',
    hourglass: 'Hourglass',
    rectangle: 'Rectangle',
    'inverted-triangle': 'Inverted Triangle'
  };

  var QUESTION_STEPS = ['shape', 'age', 'height', 'ukSize', 'rise', 'leg', 'goals'];
  var STEP_ORDER = ['intro'].concat(QUESTION_STEPS, ['done']);

  var STEP_GROUP = {
    shape: 'body_shape',
    age: 'age_range',
    height: 'height',
    ukSize: 'usualUkSize',
    rise: 'rise',
    leg: 'leg_shape',
    goals: 'fit_goals'
  };

  var SUMMARY_LABELS = {
    body_shape: 'Body shape',
    age_range: 'Age range',
    height: 'Height',
    usualUkSize: 'Usual UK size',
    rise: 'Rise',
    leg_shape: 'Fit',
    fit_goals: 'Priorities'
  };

  var AGE_DISPLAY = { '18-29': '18–29', '30-39': '30–39', '40-49': '40–49', '50+': '50+' };
  var HEIGHT_DISPLAY = { petite: 'Petite', regular: 'Regular', tall: 'Tall' };
  var RISE_DISPLAY = { Low: 'Low Rise', Mid: 'Mid Rise', High: 'High Rise' };
  var LEG_DISPLAY = { Skinny: 'Skinny', Slim: 'Slim', Straight: 'Straight', Flare: 'Flare', Wide: 'Wide Leg' };

  function safeGet() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function safeSet(profile) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
      return true;
    } catch (e) {
      return false;
    }
  }

  function emptyProfile() {
    var now = new Date().toISOString();
    return {
      version: 2,
      created_at: now,
      updated_at: now,
      body_shape: null,
      body_shape_unsure: false,
      age_range: null,
      height: null,
      usualUkSize: null,
      rise: null,
      leg_shape: [],
      fit_goals: [],
      completed: false
    };
  }

  function hasAnyAnswer(profile) {
    return !!(
      profile.body_shape ||
      profile.body_shape_unsure ||
      profile.age_range ||
      profile.height ||
      profile.usualUkSize ||
      profile.rise ||
      (Array.isArray(profile.leg_shape) ? profile.leg_shape.length : profile.leg_shape) ||
      (profile.fit_goals && profile.fit_goals.length)
    );
  }

  function Quiz(root) {
    this.root = root;
    this.profile = safeGet() || emptyProfile();
    this.persistOk = true;
    this.steps = {};
    STEP_ORDER.forEach(
      function (id) {
        this.steps[id] = root.querySelector('[data-quiz-step="' + id + '"]');
      }.bind(this)
    );
    this.progressWrap = root.querySelector('[data-quiz-progress]');
    this.progressFill = root.querySelector('[data-quiz-progress-fill]');
    this.progressLabel = root.querySelector('[data-quiz-progress-label]');
    this.returningWrap = root.querySelector('[data-quiz-returning]');
    this.jumpWrap = root.querySelector('[data-quiz-jump]');
    this.currentStep = 'intro';

    this._applyShapeParam();
    this._bindOptionGroups();
    this._bindUnsure();
    this._bindNav();
    this._renderReturningBanner();
    this._goTo('intro');
  }

  Quiz.prototype._save = function () {
    this.profile.version = 2;
    this.profile.updated_at = new Date().toISOString();
    this.persistOk = safeSet(this.profile);
  };

  Quiz.prototype._applyShapeParam = function () {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    var shape = params.get('shape');
    if (shape && SHAPE_LABELS.hasOwnProperty(shape)) {
      this.profile.body_shape = SHAPE_LABELS[shape];
      this.profile.body_shape_unsure = false;
      this._save();
    }
  };

  Quiz.prototype._bindOptionGroups = function () {
    var self = this;

    var shapeGroup = this.root.querySelector('[data-quiz-group="body_shape"]');
    if (shapeGroup) {
      shapeGroup.addEventListener('click', function (evt) {
        var btn = evt.target.closest('[data-lc-fit-shape]');
        if (!btn) return;
        var shape = btn.getAttribute('data-lc-fit-shape');
        self._selectShape(shape);
      });
    }

    this.root.querySelectorAll('[data-quiz-group]').forEach(function (group) {
      var groupName = group.getAttribute('data-quiz-group');
      if (groupName === 'body_shape') return;
      var isMulti = group.hasAttribute('data-quiz-multi');
      var maxAttr = group.getAttribute('data-quiz-max');
      var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
      group.addEventListener('click', function (evt) {
        var btn = evt.target.closest('.lc-fit-quiz-option');
        if (!btn || btn.disabled) return;
        var value = btn.getAttribute('data-quiz-value');
        if (isMulti) {
          self._toggleMulti(group, groupName, value, max);
        } else {
          self._selectSingle(group, groupName, value);
        }
      });
    });
  };

  Quiz.prototype._selectShape = function (shape) {
    var group = this.root.querySelector('[data-quiz-group="body_shape"]');
    group.querySelectorAll('[data-lc-fit-shape]').forEach(function (card) {
      var selected = card.getAttribute('data-lc-fit-shape') === shape;
      card.classList.toggle('is-selected', selected);
      if (card.tagName === 'BUTTON') {
        card.setAttribute('aria-pressed', selected ? 'true' : 'false');
      }
    });
    this.profile.body_shape = SHAPE_LABELS[shape] || shape;
    this.profile.body_shape_unsure = false;
    this._setUnsureUI(false);
    this._save();
    this._clearError('shape');
  };

  Quiz.prototype._bindUnsure = function () {
    var self = this;
    var btn = this.root.querySelector('[data-quiz-unsure]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var next = !self.profile.body_shape_unsure;
      self.profile.body_shape_unsure = next;
      if (next) {
        self.profile.body_shape = null;
        var group = self.root.querySelector('[data-quiz-group="body_shape"]');
        group.querySelectorAll('[data-lc-fit-shape]').forEach(function (card) {
          card.classList.remove('is-selected');
          if (card.tagName === 'BUTTON') card.setAttribute('aria-pressed', 'false');
        });
      }
      self._setUnsureUI(next);
      self._save();
      self._clearError('shape');
    });
  };

  Quiz.prototype._setUnsureUI = function (active) {
    var btn = this.root.querySelector('[data-quiz-unsure]');
    var panel = this.root.querySelector('[data-quiz-unsure-panel]');
    if (btn) {
      btn.classList.toggle('is-selected', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.setAttribute('aria-expanded', active ? 'true' : 'false');
    }
    if (panel) panel.hidden = !active;
  };

  Quiz.prototype._selectSingle = function (group, groupName, value) {
    group.querySelectorAll('.lc-fit-quiz-option').forEach(function (opt) {
      var selected = opt.getAttribute('data-quiz-value') === value;
      opt.classList.toggle('is-selected', selected);
      opt.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    this.profile[groupName] = groupName === 'usualUkSize' ? parseInt(value, 10) : value;
    this._save();
    this._clearErrorForGroup(groupName);
  };

  Quiz.prototype._toggleMulti = function (group, groupName, value, max) {
    var list = this.profile[groupName];
    if (!Array.isArray(list)) list = list ? [list] : [];
    var idx = list.indexOf(value);
    if (idx > -1) {
      list.splice(idx, 1);
    } else {
      if (list.length >= max) return;
      list.push(value);
    }
    this.profile[groupName] = list;
    this._save();
    this._clearErrorForGroup(groupName);

    var options = group.querySelectorAll('.lc-fit-quiz-option');
    var atMax = list.length >= max;
    options.forEach(function (opt) {
      var v = opt.getAttribute('data-quiz-value');
      var selected = list.indexOf(v) > -1;
      opt.classList.toggle('is-selected', selected);
      opt.setAttribute('aria-pressed', selected ? 'true' : 'false');
      opt.disabled = atMax && !selected;
    });

    var hint = this.root.querySelector('[data-quiz-goals-hint]');
    if (hint) {
      hint.textContent = atMax ? 'Maximum of 3 selected' : 'Choose up to 3';
    }
  };

  Quiz.prototype._clearErrorForGroup = function (groupName) {
    for (var stepId in STEP_GROUP) {
      if (STEP_GROUP[stepId] === groupName) this._clearError(stepId);
    }
  };

  Quiz.prototype._clearError = function (stepId) {
    var step = this.steps[stepId];
    if (!step) return;
    var err = step.querySelector('[data-quiz-error]');
    if (err) err.hidden = true;
  };

  Quiz.prototype._showError = function (stepId) {
    var step = this.steps[stepId];
    if (!step) return;
    var err = step.querySelector('[data-quiz-error]');
    if (err) err.hidden = false;
  };

  Quiz.prototype._validate = function (stepId) {
    if (stepId === 'shape') {
      return !!(this.profile.body_shape || this.profile.body_shape_unsure);
    }
    if (stepId === 'leg') {
      return !!(this.profile.leg_shape && this.profile.leg_shape.length > 0);
    }
    if (stepId === 'goals') {
      return this.profile.fit_goals && this.profile.fit_goals.length > 0;
    }
    var groupName = STEP_GROUP[stepId];
    if (!groupName) return true;
    return !!this.profile[groupName];
  };

  Quiz.prototype._bindNav = function () {
    var self = this;
    STEP_ORDER.forEach(function (stepId) {
      var step = self.steps[stepId];
      if (!step) return;
      var nextBtn = step.querySelector('[data-quiz-next]');
      var backBtn = step.querySelector('[data-quiz-back]');
      var finishBtn = step.querySelector('[data-quiz-finish]');
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (!self._validate(stepId)) {
            self._showError(stepId);
            return;
          }
          self._goTo(self._nextStepId(stepId));
        });
      }
      if (backBtn) {
        backBtn.addEventListener('click', function () {
          self._goTo(self._prevStepId(stepId));
        });
      }
      if (finishBtn) {
        finishBtn.addEventListener('click', function () {
          if (!self._validate(stepId)) {
            self._showError(stepId);
            return;
          }
          self.profile.completed = true;
          self._save();
          self._renderSummary();
          self._goTo('done');
        });
      }
    });

    var startBtn = this.root.querySelector('[data-quiz-start]');
    if (startBtn) {
      startBtn.addEventListener('click', function () {
        self._goTo('shape');
      });
    }

    var editBtn = this.root.querySelector('[data-quiz-edit]');
    if (editBtn) {
      editBtn.addEventListener('click', function () {
        self._goTo('shape');
      });
    }

    var retakeBtn = this.root.querySelector('[data-quiz-retake]');
    if (retakeBtn) {
      retakeBtn.addEventListener('click', function () {
        self._goTo('shape');
      });
    }
  };

  Quiz.prototype._nextStepId = function (stepId) {
    var i = STEP_ORDER.indexOf(stepId);
    return STEP_ORDER[Math.min(i + 1, STEP_ORDER.length - 1)];
  };

  Quiz.prototype._prevStepId = function (stepId) {
    var i = STEP_ORDER.indexOf(stepId);
    return STEP_ORDER[Math.max(i - 1, 0)];
  };

  Quiz.prototype._goTo = function (stepId) {
    var self = this;
    STEP_ORDER.forEach(function (id) {
      var step = self.steps[id];
      if (!step) return;
      step.hidden = id !== stepId;
    });
    this.currentStep = stepId;
    this._syncSelections(stepId);
    this._updateProgress(stepId);

    var heading = this.steps[stepId] && this.steps[stepId].querySelector('h1, h2');
    if (heading) {
      heading.focus();
    }
  };

  Quiz.prototype._syncSelections = function (stepId) {
    if (stepId === 'shape') {
      if (this.profile.body_shape) {
        var slug = Object.keys(SHAPE_LABELS).filter(
          function (k) {
            return SHAPE_LABELS[k] === this.profile.body_shape;
          }.bind(this)
        )[0];
        if (slug) this._selectShapeUIOnly(slug);
      }
      this._setUnsureUI(!!this.profile.body_shape_unsure);
      return;
    }
    var groupName = STEP_GROUP[stepId];
    if (!groupName) return;
    var group = this.root.querySelector('[data-quiz-group="' + groupName + '"]');
    if (!group) return;
    var value = this.profile[groupName];
    if (group.hasAttribute('data-quiz-multi')) {
      var list = Array.isArray(value) ? value : value ? [value] : [];
      var maxAttr = group.getAttribute('data-quiz-max');
      var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
      var atMax = list.length >= max;
      group.querySelectorAll('.lc-fit-quiz-option').forEach(function (opt) {
        var v = opt.getAttribute('data-quiz-value');
        var selected = list.indexOf(v) > -1;
        opt.classList.toggle('is-selected', selected);
        opt.setAttribute('aria-pressed', selected ? 'true' : 'false');
        opt.disabled = atMax && !selected;
      });
    } else {
      group.querySelectorAll('.lc-fit-quiz-option').forEach(function (opt) {
        var selected = opt.getAttribute('data-quiz-value') == value;
        opt.classList.toggle('is-selected', selected);
        opt.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }
  };

  Quiz.prototype._selectShapeUIOnly = function (shape) {
    var group = this.root.querySelector('[data-quiz-group="body_shape"]');
    if (!group) return;
    group.querySelectorAll('[data-lc-fit-shape]').forEach(function (card) {
      var selected = card.getAttribute('data-lc-fit-shape') === shape;
      card.classList.toggle('is-selected', selected);
      if (card.tagName === 'BUTTON') card.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  };

  Quiz.prototype._updateProgress = function (stepId) {
    var qIndex = QUESTION_STEPS.indexOf(stepId);
    if (qIndex === -1) {
      this.progressWrap.hidden = true;
      return;
    }
    this.progressWrap.hidden = false;
    var pct = ((qIndex + 1) / QUESTION_STEPS.length) * 100;
    this.progressFill.style.width = pct + '%';
    this.progressLabel.textContent = 'Question ' + (qIndex + 1) + ' of ' + QUESTION_STEPS.length;
  };

  Quiz.prototype._renderReturningBanner = function () {
    if (!hasAnyAnswer(this.profile)) return;
    this.returningWrap.hidden = false;
    var self = this;
    var labels = { shape: 'Shape', age: 'Age', height: 'Height', ukSize: 'UK Size', rise: 'Rise', leg: 'Fit', goals: 'Priorities' };
    this.jumpWrap.innerHTML = '';
    QUESTION_STEPS.forEach(function (stepId) {
      var groupName = STEP_GROUP[stepId];
      var answered =
        stepId === 'shape'
          ? !!(self.profile.body_shape || self.profile.body_shape_unsure)
          : Array.isArray(self.profile[groupName])
          ? self.profile[groupName].length > 0
          : !!self.profile[groupName];
      var pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'lc-fit-quiz__jump-item' + (answered ? ' is-answered' : '');
      pill.textContent = labels[stepId];
      pill.addEventListener('click', function () {
        self._goTo(stepId);
      });
      self.jumpWrap.appendChild(pill);
    });
  };

  Quiz.prototype._renderSummary = function () {
    var wrap = this.root.querySelector('[data-quiz-summary]');
    if (!wrap) return;
    var p = this.profile;
    var rows = [];
    rows.push(['body_shape', p.body_shape_unsure ? 'Not sure yet' : p.body_shape || '—']);
    rows.push(['age_range', AGE_DISPLAY[p.age_range] || '—']);
    rows.push(['height', HEIGHT_DISPLAY[p.height] || '—']);
    rows.push(['usualUkSize', p.usualUkSize ? 'UK ' + p.usualUkSize : '—']);
    rows.push(['rise', RISE_DISPLAY[p.rise] || '—']);
    var legList = Array.isArray(p.leg_shape) ? p.leg_shape : p.leg_shape ? [p.leg_shape] : [];
    rows.push(['leg_shape', legList.length ? legList.map(function (v) { return LEG_DISPLAY[v] || v; }).join(', ') : '—']);
    rows.push(['fit_goals', (p.fit_goals || []).join(', ') || '—']);

    wrap.innerHTML = '';
    rows.forEach(function (row) {
      var dt = document.createElement('dt');
      dt.textContent = SUMMARY_LABELS[row[0]];
      var dd = document.createElement('dd');
      dd.textContent = row[1];
      wrap.appendChild(dt);
      wrap.appendChild(dd);
    });
  };

  ns.Quiz = Quiz;

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.querySelector('[data-lc-fit-quiz]');
    if (root) {
      ns.quizInstance = new Quiz(root);
    }
  });
})(window.LustyChicFit);
